import React from 'react';
import axios from 'axios';
import { useQuery } from 'react-query';
import VBox from '@/components/layout/VBox';
import CampoTexto from '@/components/Formulario/CampoTexto';
import DataTable, { DataTableHeader } from '@/components/layout/DataTable';
import AlertBox from '@/components/layout/AlertBox';
import Skeleton from '@/components/layout/Skeleton';
import { useDebounce } from '../chat/useDebounce';
import { Catalogo, formatarMoeda, UNIDADE_PADRAO } from '@tera/shared/src/types/Catalogo';

/**
 * Prévia dos itens como o motor os enxerga, já normalizados pelo mapeamento.
 * Um mapa errado (campo de preço trocado, JSONPath que não casa) some daqui
 * como lista vazia — bem antes de aparecer numa cotação real ao cliente.
 */
const PreviaItens = ({ catalogo }: { catalogo: Catalogo }) => {
    const [busca, setBusca] = React.useState('');
    const buscaDebounce = useDebounce(busca, 500);

    const query = useQuery(
        ['catalogo/itens', catalogo.chave, buscaDebounce],
        async () => {
            const res = await axios.get(`/api/catalogo/${catalogo.chave}/itens`, {
                params: { busca: buscaDebounce || undefined, t: 50 },
            });
            return res.data;
        },
    );

    const moeda = query.data?.catalogo?.moeda ?? catalogo.moeda ?? 'BRL';

    const cabecalhos: DataTableHeader[] = [
        { label: 'Código', width: '20%', column: 'codigo' },
        { label: 'Nome', width: '35%', column: 'nome' },
        { label: 'Unidade', width: '12%', render: (l: any) => l.unidade || UNIDADE_PADRAO },
        {
            label: 'Preço unitário',
            width: '20%',
            render: (l: any) => formatarMoeda(Number(l.preco), moeda),
        },
    ];

    const itens = query.data?.result ?? [];

    return (
        <VBox gap={16}>
            <CampoTexto
                instanceId="previa-busca"
                label=""
                placeholder="Buscar item..."
                value={busca}
                onChange={setBusca}
            />

            {query.isLoading && <Skeleton format="box" />}

            {query.error && (
                <AlertBox variant="danger">
                    <p>
                        Erro ao carregar itens:{' '}
                        {(query.error as any)?.response?.data?.msg ??
                            (query.error as any)?.message ??
                            'Erro desconhecido'}
                    </p>
                </AlertBox>
            )}

            {!query.isLoading && !query.error && !itens.length && (
                <AlertBox variant="warning">
                    <p>
                        Nenhum item retornou. Confira o mapeamento dos campos — itens sem código
                        ou sem preço numérico são descartados de propósito.
                    </p>
                </AlertBox>
            )}

            {!!itens.length && <DataTable cabecalhos={cabecalhos} linhas={itens} />}
        </VBox>
    );
};

export default PreviaItens;
